import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminNotifications } from "../hooks/useApi";
import "../admin-theme.css";

type NotifItem = {
  id: string;
  type: string;
  title: string;
  message: string;
  link: string;
  created_at?: string | null;
};

const DISMISSED_KEY = "admin_dismissed_notifications";

function loadDismissed(): Set<string> {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    if (Array.isArray(arr)) return new Set(arr as string[]);
    return new Set();
  } catch {
    return new Set();
  }
}

function saveDismissed(ids: Set<string>) {
  localStorage.setItem(DISMISSED_KEY, JSON.stringify([...ids]));
}

function timeAgo(iso?: string | null): string {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "هم‌اکنون";
  if (minutes < 60) return `${minutes.toLocaleString("fa-IR")} دقیقه پیش`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours.toLocaleString("fa-IR")} ساعت پیش`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days.toLocaleString("fa-IR")} روز پیش`;
  return new Date(iso).toLocaleDateString("fa-IR");
}

const typeMeta: Record<string, { label: string; icon: React.ReactNode; bg: string; color: string; dot: string }> = {
  ticket: {
    label: "تیکت",
    bg: "var(--admin-info-bg)",
    color: "var(--admin-primary)",
    dot: "var(--admin-primary)",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
      </svg>
    ),
  },
  payment: {
    label: "پرداخت",
    bg: "var(--admin-warning-bg)",
    color: "var(--admin-warning)",
    dot: "var(--admin-warning)",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <rect x="2" y="5" width="20" height="14" rx="2" />
        <line x1="2" y1="10" x2="22" y2="10" />
      </svg>
    ),
  },
  request: {
    label: "لاگ",
    bg: "var(--admin-error-bg)",
    color: "var(--admin-error)",
    dot: "var(--admin-error)",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
      </svg>
    ),
  },
};

function getMeta(type: string) {
  return typeMeta[type] ?? typeMeta.request;
}

export default function AdminNotifications() {
  const navigate = useNavigate();
  const { data: notifications, isLoading } = useAdminNotifications();
  const [dismissed, setDismissed] = useState<Set<string>>(() => loadDismissed());
  const [filter, setFilter] = useState<"all" | "ticket" | "payment" | "request">("all");

  // Keep dismissed set in sync if another tab modifies localStorage
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === DISMISSED_KEY) setDismissed(loadDismissed());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Prune stale ids that no longer exist on server (prevents count drift after items resolve)
  useEffect(() => {
    if (!notifications?.items) return;
    const ids = new Set(notifications.items.map((n: NotifItem) => n.id));
    const hasStale = [...dismissed].some((id) => !ids.has(id));
    if (!hasStale) return;
    const next = new Set([...dismissed].filter((id) => ids.has(id)));
    setDismissed(next);
    saveDismissed(next);
  }, [notifications, dismissed]);

  const visibleItems: NotifItem[] = useMemo(() => {
    const items: NotifItem[] = (notifications?.items as NotifItem[]) || [];
    return items.filter((n) => !dismissed.has(n.id));
  }, [notifications, dismissed]);

  const filteredItems = useMemo(() => {
    if (filter === "all") return visibleItems;
    return visibleItems.filter((n) => n.type === filter);
  }, [visibleItems, filter]);

  const badgeCount = visibleItems.length;

  // Keep document badge count in sync for AdminHeader via localStorage patch:
  // AdminHeader reads notifications.unread_count; we also persist effective value so a fresh mount gets correct number.
  // Instead we just propagate via localStorage event by writing dismissed — header already filters.

  const dismissOne = (id: string) => {
    const next = new Set(dismissed);
    next.add(id);
    setDismissed(next);
    saveDismissed(next);
    // dispatch storage event for same-tab listeners
    window.dispatchEvent(new StorageEvent("storage", { key: DISMISSED_KEY }));
  };

  const dismissAll = () => {
    const next = new Set(dismissed);
    visibleItems.forEach((n) => next.add(n.id));
    setDismissed(next);
    saveDismissed(next);
    window.dispatchEvent(new StorageEvent("storage", { key: DISMISSED_KEY }));
  };

  const handleSelect = (item: NotifItem) => {
    navigate(item.link);
  };

  const tabs: { key: typeof filter; label: string }[] = [
    { key: "all", label: "همه" },
    { key: "ticket", label: "تیکت‌ها" },
    { key: "payment", label: "پرداخت‌ها" },
    { key: "request", label: "لاگ‌ها" },
  ];

  const countsByType = useMemo(() => {
    const c: Record<string, number> = { ticket: 0, payment: 0, request: 0 };
    visibleItems.forEach((n) => {
      if (c[n.type] !== undefined) c[n.type] += 1;
    });
    return c;
  }, [visibleItems]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <svg className="animate-spin h-8 w-8" viewBox="0 0 24 24" fill="none" style={{ color: "var(--admin-primary)" }}>
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold" style={{ color: "var(--admin-text-primary)" }}>
              اعلان‌ها
            </h1>
            {badgeCount > 0 ? (
              <span className="min-w-7 h-7 px-2 rounded-full bg-[#DC2626] text-white text-xs font-bold flex items-center justify-center">
                {badgeCount}
              </span>
            ) : (
              <span className="px-2.5 py-1 rounded-full text-xs font-medium" style={{ background: "var(--admin-success-bg)", color: "var(--admin-success)" }}>
                همه‌چیز مرتب است
              </span>
            )}
          </div>
          <p className="text-sm mt-2" style={{ color: "var(--admin-text-secondary)" }}>
            موارد نیازمند توجه شما — هر اعلان را بخوانید یا مستقیم به صفحهٔ مربوط بروید.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {badgeCount > 0 && (
            <button onClick={dismissAll} className="admin-btn admin-btn-secondary admin-btn-sm">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              علامت‌گذاری همه به‌عنوان خوانده‌شده
            </button>
          )}
          <button onClick={() => navigate(-1)} className="admin-btn admin-btn-secondary admin-btn-sm hidden sm:inline-flex">
            بازگشت
          </button>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="admin-card p-2 flex items-center gap-1 w-fit max-w-full overflow-x-auto">
        {tabs.map((t) => {
          const count = t.key === "all" ? badgeCount : countsByType[t.key] ?? 0;
          const isActive = filter === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setFilter(t.key)}
              className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all flex items-center gap-2 ${isActive ? "text-white shadow-sm" : ""}`}
              style={
                isActive
                  ? { background: "var(--admin-primary)", color: "#fff" }
                  : { color: "var(--admin-text-secondary)" }
              }
            >
              {t.label}
              <span
                className="min-w-5 h-5 px-1 rounded-full text-[11px] font-bold flex items-center justify-center"
                style={{
                  background: isActive ? "rgba(255,255,255,0.22)" : "var(--admin-bg-hover)",
                  color: isActive ? "#fff" : "var(--admin-text-secondary)",
                  border: isActive ? "1px solid rgba(255,255,255,0.25)" : "1px solid var(--admin-border)",
                }}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* List */}
      <div className="admin-card overflow-hidden">
        <div className="admin-card-header">
          <h2 className="admin-card-title">
            {filter === "all" ? "همه اعلان‌ها" : filter === "ticket" ? "تیکت‌ها" : filter === "payment" ? "پرداخت‌ها" : "لاگ‌ها"}
          </h2>
          <span className="admin-badge admin-badge-info">{filteredItems.length} مورد</span>
        </div>

        {filteredItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: "var(--admin-primary-light)" }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--admin-primary)" strokeWidth="1.5" strokeLinecap="round">
                <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 01-3.46 0" />
              </svg>
            </div>
            <h3 className="text-base font-semibold mb-1" style={{ color: "var(--admin-text-primary)" }}>
              {badgeCount === 0 ? "اعلانی وجود ندارد" : "موردی در این دسته نیست"}
            </h3>
            <p className="text-sm max-w-sm" style={{ color: "var(--admin-text-tertiary)" }}>
              {badgeCount === 0
                ? "در حال حاضر هیچ مورد نیازمند توجهی وجود ندارد. بعداً دوباره سر بزنید."
                : "فیلتر دیگری را امتحان کنید یا به تب «همه» برگردید."}
            </p>
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: "var(--admin-border-light)" }}>
            {filteredItems.map((item) => {
              const meta = getMeta(item.type);
              return (
                <div
                  key={item.id}
                  className="group flex items-stretch gap-4 p-4 sm:p-5 hover:bg-[var(--admin-bg-hover)] transition-colors"
                >
                  {/* Icon */}
                  <div
                    className="w-10 h-10 sm:w-11 sm:h-11 shrink-0 rounded-xl flex items-center justify-center mt-0.5"
                    style={{ background: meta.bg, color: meta.color }}
                  >
                    {meta.icon}
                  </div>

                  {/* Content — clickable to navigate */}
                  <button
                    type="button"
                    onClick={() => handleSelect(item)}
                    className="flex-1 min-w-0 text-right flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-4 group-hover:opacity-100"
                  >
                    <span className="flex-1 min-w-0 text-right">
                      <span className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold truncate" style={{ color: "var(--admin-text-primary)" }}>
                          {item.title}
                        </span>
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium"
                          style={{ background: meta.bg, color: meta.color }}
                        >
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.dot }} />
                          {meta.label}
                        </span>
                        {item.created_at && (
                          <span className="text-xs" style={{ color: "var(--admin-text-tertiary)" }}>
                            {timeAgo(item.created_at)}
                          </span>
                        )}
                      </span>
                      <span className="block text-sm mt-1 leading-relaxed text-right line-clamp-2" style={{ color: "var(--admin-text-secondary)" }}>
                        {item.message}
                      </span>
                      <span className="inline-flex items-center gap-1.5 mt-2 text-xs font-medium" style={{ color: "var(--admin-primary)" }}>
                        مشاهده جزئیات
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <polyline points="15 18 9 12 15 6" />
                        </svg>
                      </span>
                    </span>
                  </button>

                  {/* Actions */}
                  <div className="flex flex-col items-end gap-2 shrink-0 justify-center">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        dismissOne(item.id);
                      }}
                      className="admin-btn admin-btn-secondary admin-btn-sm whitespace-nowrap"
                      title="این اعلان دیگر در شمارش نوار بالا حساب نشود"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      خوانده شده
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSelect(item)}
                      className="hidden sm:inline-flex items-center gap-1 text-xs font-medium hover:underline"
                      style={{ color: "var(--admin-text-tertiary)" }}
                    >
                      رفتن به صفحه
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer hint */}
      <p className="text-xs text-center" style={{ color: "var(--admin-text-tertiary)" }}>
        کلیک روی هر ردیف شما را به صفحهٔ مربوطه می‌برد — تیکت‌ها به /admin/tickets و لاگ‌ها به /admin/logs.
      </p>
    </div>
  );
}
